import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, getIn, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import AutoCompleteFormik from '~/components/AutocompleteFormik';
import {
  filterFlagKeysInScope,
  getClaimFlagFilterLabel,
  getClaimFlagFilterOptions,
} from '~/components/ClaimFlags/claimFlagFilterUtils';
import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import { localeDetails } from '~/components/CmsMain/localeGlobals';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { yupPhoneValidation } from '~/Utils/phoneValidationUtils';
import { getPostalCodeYupTestParams } from '~/Utils/postalCodeUtils';

import { CONSTANTS, COUNTRIES_DICT } from '../../Types';
import { formatSortOrder } from '../../Utils';
import cn from '../../Utils/cn';
import CardDialog from '../CardDialog';
import CheckboxFormik from '../CheckboxFormik';
import ClaimsTable from '../ClaimsTable';
import { Heading, Text } from '../core';
import LoadingIndicator from '../LoadingIndicator';
import useOrganization from '../OrganizationContext';
import TextFieldFormik, {
  DatePickerTextFieldFormik,
  MultiSelectTextFieldFormik,
  YearPickerSelectTextFieldFormik,
} from '../TextFieldFormik';
import { LobSelectFormik } from '../TPA/LOB/LobSelectFormik';
import SubOrganizationSelectFormik from '../TPA/SubOrganizations/SubOrganizationSelectFormik';
import useDataFetcher from '../useDataFetcher';

import { useStyles } from '../../assets/styles';
import colors from '../../assets/colors.module.scss';

const DEFAULT_SORT_BY_COLUMN = { id: 'date_of_loss' };
const DEFAULT_CLAIMS_PER_PAGE = 15;
const ROWS_PER_PAGE_MIN = 10;
const ROWS_PER_PAGE_MAX = 30;

export const FILTER_PARAMS_INITIAL_VALUES = {
  handling_adjuster: [],
  stage_key: [],
  flag_key: [],
  page: 0,
  rows_per_page: DEFAULT_CLAIMS_PER_PAGE,
  sort_by_column: DEFAULT_SORT_BY_COLUMN,
  status: '',
  sub_organization_id: '',
  lob: '',
  claim_date_of_loss_start_date: '',
  claim_date_of_loss_end_date: '',
  claim_reported_date_start_date: '',
  claim_reported_date_end_date: '',
  incident_type: '',
  claim_number: '',
  policy_number: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_ssn: '',
  contact_company_name: '',
  vehicle_year: '',
  vehicle_make: '',
  vehicle_model: '',
  vehicle_vin: '',
  vehicle_plate_number: '',
  zipcode: '',
  phone_number: '',
  is_company: '',
  external_claim_number: '',
  email_address: '',
  policy_country: '',
};

const NULL_CHOICE_VALUE = 'NONE';
// Shared so the LobSelectFormik fieldId and every setFieldValue/read of that field can't drift apart.
const LOB_FIELD_ID = 'lob';

const AdvancedClaimsSearchPage = () => {
  const [showResults, setShowResults] = useState(false);
  const [filterParams, setFilterParams] = useState(FILTER_PARAMS_INITIAL_VALUES);
  const { page, sort_by_column } = filterParams;
  const [isSearchOpen, setIsSearchOpen] = useState(true);
  const config = { params: { ...filterParams, page: page + 1, sort_by_column: formatSortOrder(sort_by_column) } }; // page in the flask-SQLAlchemy is 1-based
  const { isLoading, data: { count, claims } = {} } = useDataFetcher(
    showResults ? '/api/v1/claims/advanced_claims_search' : undefined,
    config
  );
  const { isOperational: isClaimStageOperational } = useClaimStageConfigurations();

  const onSubmit = (values) => {
    setIsSearchOpen(false);
    setShowResults(true);
    setFilterParams(values);
  };

  const onReset = (values) => {
    setShowResults(false);
    setFilterParams(values);
  };

  const onChangePage = (_, newPage) => {
    setFilterParams({
      ...filterParams,
      page: newPage,
    });
  };

  const onChangeRowsPerPage = (event) => {
    setFilterParams({
      ...filterParams,
      rows_per_page: parseInt(event.target.value),
      page: 0,
    });
  };

  const onSortByColumn = (_, sort_by_column) => {
    setFilterParams({
      ...filterParams,
      sort_by_column,
      page: 0, // reset the page when sorting
    });
  };

  return (
    <>
      <div>
        <CollapsibleWrapper
          title="Search Claims"
          actionCard
          noBorder
          open={isEmpty(claims) || isSearchOpen} //we add the isEmpty(claims) to insure that it collapses after the results are shown
          onCollapse={() => setIsSearchOpen((prevOpen) => !prevOpen)}
          unmountOnExit={false}
          isActionCardDrawer={false}
        >
          <AdvancedClaimsSearchDialog onSubmit={onSubmit} onReset={onReset} />
        </CollapsibleWrapper>
      </div>
      {isLoading && showResults && <LoadingIndicator />}
      {!isLoading && showResults && (
        <>
          {!isEmpty(claims) ? (
            <>
              <Text className="pt-20" weight={Text.WEIGHTS.MEDIUM} variant={Text.VARIANTS.LG}>
                Search Results
              </Text>
              <ClaimsTable
                claims={claims}
                hideLastTouchedDate
                hideClaimStage={!isClaimStageOperational}
                paginationProps={{
                  page,
                  rowsPerPage: filterParams?.rows_per_page,
                  onChangePage,
                  count,
                  onChangeRowsPerPage,
                  rowsPerPageOptions: [
                    ROWS_PER_PAGE_MIN,
                    DEFAULT_CLAIMS_PER_PAGE,
                    CONSTANTS.DEFAULT_CLAIMS_PER_PAGE,
                    ROWS_PER_PAGE_MAX,
                  ],
                }}
                onSortByColumn={onSortByColumn}
              />
            </>
          ) : (
            <Text weight={Text.WEIGHTS.MEDIUM} variant={Text.VARIANTS.LG}>
              We couldn’t find any claims which match your current search, try refining your search
            </Text>
          )}
        </>
      )}
    </>
  );
};

AdvancedClaimsSearchPage.propTypes = {
  onReportNewFnol: PropTypes.func.isRequired,
};

const AdvancedClaimsSearchDialog = ({ onSubmit, onReset }) => {
  const { classes } = useStyles();

  return (
    <Formik
      initialValues={FILTER_PARAMS_INITIAL_VALUES}
      validationSchema={Yup.object().shape({
        status: Yup.string().nullable(),
        lob: Yup.string().nullable(),
        claim_date_of_loss_start_date: Yup.date().nullable(),
        claim_date_of_loss_end_date: Yup.date().nullable(),
        claim_reported_date_start_date: Yup.date().nullable(),
        claim_reported_date_end_date: Yup.date().nullable(),
        external_claim_number: Yup.string().nullable(),
        incident_type: Yup.string().nullable(),
        claim_number: Yup.string().nullable(),
        policy_number: Yup.string().nullable(),
        contact_first_name: Yup.string().nullable(),
        contact_last_name: Yup.string().nullable(),
        contact_ssn: Yup.number().nullable(),
        contact_company_name: Yup.string().nullable(),
        vehicle_year: Yup.string().nullable(),
        vehicle_make: Yup.string().nullable(),
        vehicle_model: Yup.string().nullable(),
        vehicle_vin: Yup.string().nullable(),
        vehicle_plate_number: Yup.string().nullable(),
        email_address: Yup.string().nullable().email('Must be a valid email'),
        policy_country: Yup.string().nullable().oneOf(Object.keys(COUNTRIES_DICT)),
        zipcode: Yup.string()
          .nullable()
          .test(...getPostalCodeYupTestParams(false)),
        phone_number: yupPhoneValidation.nullable(),
        is_company: Yup.boolean().nullable(),
        stage_key: Yup.array().of(Yup.string().nullable()).nullable(),
        flag_key: Yup.array().of(Yup.string().nullable()).nullable(),
      })}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      onReset={(values, { setSubmitting }) => {
        onReset(values);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, resetForm }) => (
        <CardDialog containerStyle={{ background: colors.grey_02 }}>
          <div className="grid">
            <AdvancedClaimsSearchFiltersFormik />
            <div className="flex gap-12">
              <div className="mt-4">
                <Button onClick={resetForm} variant="contained" className={classes.cancelButton}>
                  Clear
                </Button>
              </div>
              <div>
                <Button
                  className={cn(classes.button, 'm-4, ml-20')}
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                >
                  Search
                </Button>
              </div>
            </div>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

AdvancedClaimsSearchDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export const AdvancedClaimsSearchFiltersFormik = () => {
  const { classes } = useStyles();
  const { isSubmitting, values, setFieldValue } = useFormikContext();
  const { subOrganizationEnabled, orgLevelSupportedLobs, isMultipleCountriesEnabled } = useOrganization();
  const { data: adjustersData = [] } = useDataFetcher('/api/v1/users/possible_adjusters', {});
  const possibleFileOwners = useMemo(
    () => [{ username: 'Unassigned FNOLs', id: null }, ...adjustersData],
    [adjustersData]
  );
  const possibleFileOwnersById = useMemo(
    () => possibleFileOwners.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {}),
    [possibleFileOwners]
  );

  const subOrganizationIds = useMemo(() => {
    const subOrganization = getIn(values, 'sub_organization_id');
    return subOrganization ? [subOrganization] : [];
  }, [values]);

  const { isOperational: isClaimStageOperational, claimStageConfigurations } = useClaimStageConfigurations();

  const displayedClaimStageOptions = isClaimStageOperational
    ? claimStageConfigurations
        .filter((config) => config.is_enabled)
        .map((config) => config.key)
        .concat(NULL_CHOICE_VALUE)
    : [];

  const getClaimStageLabel = (stageKey) =>
    stageKey === NULL_CHOICE_VALUE
      ? 'No stage set'
      : claimStageConfigurations.find((config) => config.key === stageKey)?.label;

  const { claimFlagConfigurations } = useClaimFlagConfigurations();

  // Offer only flags in scope for the selected sub-org / LOB (mirrors the backend scope check), so a
  // user can't pick a flag that would match zero claims here. Gate visibility on the same scoped result —
  // a sub-org/LOB with no in-scope flag shouldn't render a filter whose only option is "No flags".
  const displayedClaimFlagOptions = getClaimFlagFilterOptions(claimFlagConfigurations, {
    subOrganizationId: getIn(values, 'sub_organization_id'),
    lob: getIn(values, LOB_FIELD_ID),
  });
  const shouldShowClaimFlags = displayedClaimFlagOptions.length > 1;

  const getClaimFlagLabel = (flagKey) => getClaimFlagFilterLabel(claimFlagConfigurations, flagKey);

  // Narrowing the sub-org / LOB scope can strand a now-out-of-scope flag key in the selection; prune it
  // so it isn't silently sent (which would empty the results while the chip still reads as selected).
  // Called from every path that moves either scope axis (sub-org onChange, its programmatic LOB reset,
  // and the LOB onChange) so the pruning fires on all of them, not just direct interaction with one field.
  const handleFlagScopeChange = (subOrganizationId, lob) => {
    const selectedFlagKeys = getIn(values, 'flag_key') ?? [];
    const scopedFlagKeys = filterFlagKeysInScope(selectedFlagKeys, claimFlagConfigurations, {
      subOrganizationId,
      lob,
    });
    if (scopedFlagKeys.length !== selectedFlagKeys.length) {
      setFieldValue('flag_key', scopedFlagKeys);
    }
  };

  // The scope axes are not the only way an option can disappear: a flag disabled (or re-scoped) elsewhere
  // arrives as a reloaded claimFlagConfigurations, dropping the option while the selection survives and
  // keeps narrowing the search invisibly. Prune on that reload too — but only when the configs actually
  // change, never on mount, where an unresolved fetch still reads as [] and would wipe the selection.
  const selectedFlagKeys = getIn(values, 'flag_key');
  const flagScopeSubOrganizationId = getIn(values, 'sub_organization_id');
  const flagScopeLob = getIn(values, LOB_FIELD_ID);
  const lastPrunedFlagConfigurations = useRef(claimFlagConfigurations);
  useEffect(() => {
    if (lastPrunedFlagConfigurations.current === claimFlagConfigurations) {
      return;
    }
    lastPrunedFlagConfigurations.current = claimFlagConfigurations;
    const selected = selectedFlagKeys ?? [];
    const scoped = filterFlagKeysInScope(selected, claimFlagConfigurations, {
      subOrganizationId: flagScopeSubOrganizationId,
      lob: flagScopeLob,
    });
    if (scoped.length !== selected.length) {
      setFieldValue('flag_key', scoped);
    }
  }, [claimFlagConfigurations, selectedFlagKeys, flagScopeSubOrganizationId, flagScopeLob, setFieldValue]);

  const shouldShowSsn = localeDetails.locale.region === 'US';

  const onSubOrganizationChange = (event) => {
    const subOrganizationId = event.target.value;
    const lob = FILTER_PARAMS_INITIAL_VALUES[LOB_FIELD_ID];
    setFieldValue('sub_organization_id', subOrganizationId);
    setFieldValue(LOB_FIELD_ID, lob); // sub-org change also resets LOB, so both scope axes move at once
    handleFlagScopeChange(subOrganizationId, lob);
  };

  const onLobChange = (event) => {
    const lob = event.target.value;
    setFieldValue(LOB_FIELD_ID, lob);
    // A LOB change doesn't touch the sub-org, so prune against the sub-org currently in the form.
    handleFlagScopeChange(getIn(values, 'sub_organization_id'), lob);
  };

  const onMultiSelectChange = (fieldId, nextValues, emptySelectionValue = 'All') => {
    if (nextValues.includes(emptySelectionValue)) {
      setFieldValue(fieldId, []);
    } else {
      setFieldValue(fieldId, nextValues);
    }
  };

  return (
    <div>
      <Heading variant={Heading.TYPES.H3}>General Details</Heading>
      <div className="mb-32 grid grid-cols-5 gap-32">
        <div>
          <MultiSelectTextFieldFormik
            id="handling_adjuster"
            label="File / Exposure owner"
            disabled={isSubmitting}
            options={Object.keys(possibleFileOwnersById)}
            renderValue={(selectedIds) =>
              selectedIds.map((selectedId) => possibleFileOwnersById[selectedId]?.username).join(', ')
            }
            renderOption={(val) => possibleFileOwnersById[val].username}
            className={classes.formTextFieldNoErrorSpacing}
            fullWidth
            sortAlphabetic
            addAllOption
            allOptionValue="All"
            onChange={(nextValues) => onMultiSelectChange('handling_adjuster', nextValues)}
          />
        </div>

        {subOrganizationEnabled ? (
          <div>
            <SubOrganizationSelectFormik
              fieldId="sub_organization_id"
              disabled={isSubmitting}
              onChange={onSubOrganizationChange}
            />
          </div>
        ) : null}

        <div>
          <LobSelectFormik
            fieldId={LOB_FIELD_ID}
            subOrganizationIds={subOrganizationIds}
            disabled={isSubmitting}
            onChange={onLobChange}
          />
        </div>

        <div>
          <TextFieldFormik
            id="status"
            label="Status"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
            select
          >
            <MenuItem value={null}>All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="reopened">Reopened</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextFieldFormik>
        </div>

        {isMultipleCountriesEnabled && (
          <div>
            <AutoCompleteFormik
              id="policy_country"
              label="Policy Country"
              className="-mt-2 ml-2 mr-2"
              options={Object.keys(COUNTRIES_DICT)}
              getOptionLabel={(countryKey) => COUNTRIES_DICT[countryKey]}
            />
          </div>
        )}
      </div>

      <Heading variant={Heading.TYPES.H3}>Claim Details</Heading>

      <div className="mb-32 grid grid-cols-5 gap-32">
        <div>
          <TextFieldFormik
            id="claim_number"
            label="Claim Number"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
        <div>
          <TextFieldFormik
            id="external_claim_number"
            label="External Number"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
        <div>
          <TextFieldFormik
            id="policy_number"
            label="Policy Number"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
        {isClaimStageOperational && (
          <MultiSelectTextFieldFormik
            id="stage_key"
            label="Stage"
            className={cn(classes.textField, 'w-full')}
            options={displayedClaimStageOptions}
            renderValue={(selectedKeys) => selectedKeys.map(getClaimStageLabel).join(', ')}
            renderOption={getClaimStageLabel}
            addAllOption
            allOptionValue="All"
            onChange={(nextValues) => onMultiSelectChange('stage_key', nextValues)}
          />
        )}
        {shouldShowClaimFlags && (
          <MultiSelectTextFieldFormik
            id="flag_key"
            label="Flags"
            className={cn(classes.textField, 'w-full')}
            options={displayedClaimFlagOptions}
            renderValue={(selectedKeys) => selectedKeys.map(getClaimFlagLabel).join(', ')}
            renderOption={getClaimFlagLabel}
            addAllOption
            allOptionValue="All"
            onChange={(nextValues) => onMultiSelectChange('flag_key', nextValues)}
          />
        )}
      </div>
      <div className="mb-32 grid grid-cols-5 gap-32">
        <div>
          <DatePickerTextFieldFormik
            id="claim_date_of_loss_start_date"
            label="Date of loss Start Date"
            className={classes.textField}
            disableFuture
            fullWidth
            disabled={isSubmitting}
            clearable
          />
        </div>

        <div>
          <DatePickerTextFieldFormik
            id="claim_date_of_loss_end_date"
            label="Date of loss End Date"
            className={classes.textField}
            disableFuture
            fullWidth
            disabled={isSubmitting}
            clearable
          />
        </div>

        <div>
          <DatePickerTextFieldFormik
            id="claim_reported_date_start_date"
            label="Report Start Date"
            className={classes.textField}
            disableFuture
            fullWidth
            disabled={isSubmitting}
            clearable
          />
        </div>

        <div>
          <DatePickerTextFieldFormik
            id="claim_reported_date_end_date"
            label="Report End Date"
            className={classes.textField}
            disableFuture
            fullWidth
            disabled={isSubmitting}
            clearable
          />
        </div>
      </div>

      <Heading variant={Heading.TYPES.H3}>Contact Details</Heading>

      <div className="mb-32 grid grid-cols-5 gap-32">
        <div className="mt-12">
          <CheckboxFormik id="is_company" label="Contact is a Company" />
        </div>
        {values['is_company'] ? (
          <div>
            <TextFieldFormik
              id="contact_company_name"
              label="Company Name"
              className={classes.textField}
              fullWidth
              disabled={isSubmitting}
            />
          </div>
        ) : (
          <>
            <div>
              <TextFieldFormik
                id="contact_first_name"
                label="First Name"
                className={classes.textField}
                fullWidth
                disabled={isSubmitting}
              />
            </div>

            <div>
              <TextFieldFormik
                id="contact_last_name"
                label="Last Name"
                className={classes.textField}
                fullWidth
                disabled={isSubmitting}
              />
            </div>
            {shouldShowSsn && (
              <div>
                <TextFieldFormik
                  id="contact_ssn"
                  label="Social Security Number"
                  className={classes.textField}
                  fullWidth
                  disabled={isSubmitting}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="mb-20 grid grid-cols-5 gap-32">
        <div>
          <TextFieldFormik
            id="zipcode"
            label="Zipcode"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
        <div>
          <TextFieldFormik
            id="phone_number"
            label="Phone Number"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
        <div>
          <TextFieldFormik
            id="email_address"
            label="Email Address"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
      </div>

      {orgLevelSupportedLobs.includes('auto_claim') && (
        <>
          <Heading variant={Heading.TYPES.H3}>Auto Specific</Heading>
          <div className="mb-20 grid grid-cols-5 gap-32">
            <div>
              <YearPickerSelectTextFieldFormik
                id="vehicle_year"
                label="Year"
                fullWidth
                disabled={isSubmitting}
                className={classes.textField}
              />
            </div>
            <div>
              <TextFieldFormik
                id="vehicle_make"
                label="Make"
                fullWidth
                disabled={isSubmitting}
                className={classes.textField}
              />
            </div>
            <div>
              <TextFieldFormik
                id="vehicle_model"
                label="Model"
                fullWidth
                disabled={isSubmitting}
                className={classes.textField}
              />
            </div>
            <div>
              <TextFieldFormik
                id="vehicle_vin"
                label="VIN"
                fullWidth
                disabled={isSubmitting}
                className={classes.textField}
              />
            </div>
            <div>
              <TextFieldFormik
                id="vehicle_plate_number"
                label="Plate Number"
                fullWidth
                disabled={isSubmitting}
                className={classes.textField}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

AdvancedClaimsSearchFiltersFormik.propTypes = {};

export default AdvancedClaimsSearchPage;
